import {
  Alert,
  Autocomplete,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
} from '@mui/material';
import { LoadingButton } from '@mui/lab';
import { classValidatorResolver } from '@hookform/resolvers/class-validator';
import { Controller, SubmitHandler, useForm } from 'react-hook-form';
import { useNavigate, useParams } from 'react-router-dom';
import {
  useAddDomainToUser,
  useAddRoleToUser,
  useCreateUser,
  useGetUser,
  useRemoveDomainFromUser,
  useRemoveRoleFromUser,
  useUpdateUser,
} from '../../hooks/api/use-users.hook';
import { UserApiModel, UserRelationship, WithDomains, WithRoles } from '../../../../types/models/user.model';
import { LoadingIndicator } from '../LoadingIndicator';
import { useSearchDomains } from '../../hooks/api/use-domains.hook';
import { useSearchRoles } from '../../hooks/api/use-roles.hook';
import { UserForm } from './UserForm';
import { getDirtyValues } from '../../utils/react-hook-form';

export const AddEditUser = () => {
  const navigate = useNavigate();
  const { userId } = useParams<{ userId: string }>();

  const {
    user,
    loading: loadingUser,
    error: gettingError,
  } = useGetUser<UserApiModel & WithRoles & WithDomains>({
    userId: Number(userId),
    include: [UserRelationship.ROLES, UserRelationship.DOMAINS],
  });
  const [createUser, { loading: creatingUser, error: createError }] = useCreateUser();
  const [updateUser, { loading: updatingUser, error: updateError }] = useUpdateUser();
  const [assignRoleToUser] = useAddRoleToUser();
  const [removeRoleFromUser] = useRemoveRoleFromUser();
  const [assignDomainToUser] = useAddDomainToUser();
  const [removeDomainFromUser] = useRemoveDomainFromUser();
  const { loading: loadingRoles, response: rolesResponse, error: rolesError } = useSearchRoles({});
  const { loading: loadingDomains, response: domainsResponse, error: domainsError } = useSearchDomains({});

  const {
    control,
    register,
    handleSubmit,
    formState: { errors, dirtyFields, isSubmitting },
  } = useForm<UserForm>({ resolver: classValidatorResolver(UserForm), values: user });

  const onSubmit: SubmitHandler<UserForm> = async (submittedValues) => {
    if (!user) {
      const { roles, domains, ...values } = submittedValues;
      await createUser({
        ...values,
        password: values.password || '',
        roleIds: roles.map(({ id }) => id),
        domainIds: domains.map(({ id }) => id),
      });
    } else {
      const { roles, domains, ...values } = getDirtyValues(dirtyFields, submittedValues);
      const rolesToAdd = roles?.filter(({ id }) => !user.roles.find((role) => role.id === id)) ?? [];
      const rolesToRemove = roles ? user.roles.filter(({ id }) => !roles?.find((role) => role.id === id)) : [];
      const domainsToAdd = domains?.filter(({ id }) => !user.domains.find((domain) => domain.id === id)) ?? [];
      const domainsToRemove = domains
        ? user.domains.filter(({ id }) => !domains?.find((domain) => domain.id === id))
        : [];

      await Promise.all([
        updateUser({ ...values, userId: user.id, password: values.password || undefined }),
        ...rolesToAdd.map(({ id: roleId }) => assignRoleToUser({ userId: user.id, roleId })),
        ...rolesToRemove.map(({ id: roleId }) => removeRoleFromUser({ userId: user.id, roleId })),
      ]);

      // Due to user-domain relationship being incorrectly managed in the schema, we have to save these one by one
      // Otherwise the updates aren't reflected correctly
      for (const domain of domainsToAdd) {
        // eslint-disable-next-line no-await-in-loop
        await assignDomainToUser({ userId: user.id, domainId: domain.id });
      }
      for (const domain of domainsToRemove) {
        // eslint-disable-next-line no-await-in-loop
        await removeDomainFromUser({ userId: user.id, domainId: domain.id });
      }
    }

    navigate('..');
  };

  const renderContent = () => {
    if (gettingError || rolesError || domainsError) {
      return <Alert severity="error">{(gettingError || rolesError || domainsError)?.response?.data?.message}</Alert>;
    }

    return (
      <>
        <TextField
          {...register('firstName')}
          variant="outlined"
          sx={{ mt: '1rem' }}
          disabled={creatingUser || updatingUser}
          error={Boolean(errors.firstName)}
          helperText={errors.firstName?.message}
          label="First name"
          margin="dense"
          size="small"
          fullWidth
        />
        <TextField
          {...register('lastName')}
          variant="outlined"
          sx={{ mt: '1rem' }}
          disabled={creatingUser || updatingUser}
          error={Boolean(errors.lastName)}
          helperText={errors.lastName?.message}
          label="Last name"
          margin="dense"
          size="small"
          fullWidth
        />
        <TextField
          {...register('email')}
          variant="outlined"
          sx={{ mt: '1rem' }}
          disabled={creatingUser || updatingUser}
          error={Boolean(errors.email)}
          helperText={errors.email?.message}
          label="Email"
          margin="dense"
          size="small"
          fullWidth
        />
        <TextField
          {...register('password')}
          variant="outlined"
          sx={{ mt: '1rem' }}
          disabled={creatingUser || updatingUser}
          error={Boolean(errors.password)}
          helperText={errors.password?.message}
          label="Password"
          margin="dense"
          size="small"
          type="password"
          fullWidth
        />
        <Controller
          control={control}
          name="roles"
          render={({ field }) => (
            <Autocomplete
              {...register('roles')}
              loading={loadingRoles}
              sx={{ mt: '1rem' }}
              multiple
              onChange={(_event, selectedOptions) => field.onChange(selectedOptions)}
              onBlur={field.onBlur}
              value={field.value}
              getOptionLabel={(role) => role?.name ?? ''}
              options={rolesResponse?.pages.map(({ result }) => result).flat() ?? []}
              renderInput={(params) => (
                <TextField {...params} label="Roles" error={Boolean(errors.roles)} helperText={errors.roles?.message} />
              )}
            />
          )}
        />
        <Controller
          control={control}
          name="domains"
          render={({ field }) => (
            <Autocomplete
              {...register('domains')}
              loading={loadingDomains}
              sx={{ mt: '1rem' }}
              multiple
              onChange={(_event, selectedOptions) => field.onChange(selectedOptions)}
              onBlur={field.onBlur}
              value={field.value}
              getOptionLabel={(domain) => domain?.shortName ?? ''}
              options={domainsResponse?.pages.map(({ result }) => result).flat() ?? []}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Domains"
                  error={Boolean(errors.domains)}
                  helperText={errors.domains?.message}
                />
              )}
            />
          )}
        />
      </>
    );
  };

  if (loadingUser) {
    // FIXME: workaround for react-use-form not resetting controlled fields in a dialog
    return <LoadingIndicator />;
  }

  return (
    <Dialog open={true}>
      {loadingUser ? (
        <LoadingIndicator />
      ) : (
        <form onSubmit={handleSubmit(onSubmit)}>
          <DialogTitle>{`${user ? 'Edit' : 'Add'} User`}</DialogTitle>
          <DialogContent>
            {renderContent()}
            {createError || updateError ? (
              <Alert sx={{ mt: '1rem' }} severity="error">
                {createError?.response?.data?.message || updateError?.response?.data?.message}
              </Alert>
            ) : null}
          </DialogContent>
          <DialogActions>
            <Button disabled={isSubmitting} variant="outlined" onClick={() => navigate('..')} color="primary">
              Cancel
            </Button>
            <LoadingButton
              color="primary"
              type="submit"
              variant="contained"
              loading={isSubmitting || Boolean(gettingError)}
            >
              {user ? 'Save' : 'Create'}
            </LoadingButton>
          </DialogActions>
        </form>
      )}
    </Dialog>
  );
};
